"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { CountrySelectWithSearch } from "@/components/booking/CountrySelect";
import { PHONE_LABELS } from "@/components/booking/phone-labels";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, Check, ChevronDown, Loader2, Menu, X } from "lucide-react";
import {
  btnGhost,
  btnPrimary,
  eyebrowClass,
  h2Class,
  leadClass,
} from "@/components/editorial";
import {
  trackEvent,
  trackMetaStandardEvent,
  trackGoogleAdsLead,
} from "@/lib/analytics";
import BoundaryDiagram from "./illustrations";

// ──────────────────────────────────────────────────────────────────────────
// EVENT CONFIG — single source of truth for the page. The confirmation email
// and .ics live in app/api/webinar-register/route.ts under the same `source`
// key; change the date in BOTH places.
// ──────────────────────────────────────────────────────────────────────────
export const WEBINAR = {
  source: "webinar-limite",
  startsAt: "2026-09-30T19:00:00+03:00",
  endsAt: "2026-09-30T20:30:00+03:00",
  dateLabel: "Miercuri, 30 septembrie",
  dateShort: "30 sept",
  timeLabel: "19:00",
  durationLabel: "90 de minute",
  format: "Online, pe Google Meet",
  title: "De ce este atât de greu să punem limite?",
  speakerName: "Ioana",
  speakerFullName: "Ioana Ivan",
  speakerRole: "Psiholog clinician și psihoterapeut psihanalitic",
  // Left on the id on purpose: profiles moved to /terapeuti/<nume> and the id
  // 301s there, which is safer than hardcoding a slug nobody has verified
  // against the row.
  speakerProfileUrl: "/terapeuti/ioana-ivan",
  // Media lives in /public for now. Point this at the bucket once the files are
  // uploaded (e.g. "https://media.mindtale.ro/webinar-limite") and delete them
  // from the repo — nothing else on the page needs to change.
  mediaBase: process.env.NEXT_PUBLIC_WEBINAR_LIMITE_MEDIA ?? "/webinar-limite",
};

const GDPR_URL = "https://app.mindtale.ro/termeni-si-conditii?doc=gdpr";
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

function googleCalUrl(): string {
  const fmt = (iso: string) =>
    new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const text = encodeURIComponent(`Webinar Mindtale: ${WEBINAR.title}`);
  const details = encodeURIComponent(
    "Webinar gratuit despre limite personale. Link-ul de acces ajunge pe email.",
  );
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(WEBINAR.startsAt)}/${fmt(WEBINAR.endsAt)}&details=${details}`;
}

/** Fade-up reveal on scroll. Respects reduced-motion; 800ms mount fallback so
 *  content never stays invisible on flaky mobile WebViews. */
function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setMounted(true), 800);
    return () => window.clearTimeout(t);
  }, []);

  if (reduce || mounted) {
    return (
      <motion.div
        className={className}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay, ease: [0.2, 0.65, 0.35, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
      transition={{ duration: 0.5, delay, ease: [0.2, 0.65, 0.35, 1] }}
    >
      {children}
    </motion.div>
  );
}

function scrollToForm(location: string) {
  trackEvent("webinar_lp_cta_click", { webinar: "limite", location });
  document
    .getElementById("inscriere")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ──────────────────────────────────────────────────────────────────────────
export default function WebinarLimiteClient({
  closed = false,
}: {
  closed?: boolean;
}) {
  useEffect(() => {
    trackEvent("webinar_lp_view", { webinar: "limite", closed });
  }, [closed]);

  if (closed) return <ClosedPage />;

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-20 sm:pb-0">
      <MinimalHeader />

      <Hero />

      <TrustBand />

      <Reveal><Recognition /></Reveal>

      <PullQuote />

      <Reveal><TheDistinction /></Reveal>

      <ThreePatterns />

      <Reveal><WhatYouLeaveWith /></Reveal>

      <Reveal><Semafor /></Reveal>

      <Reveal><Speaker /></Reveal>

      <Agenda />

      <Registration />

      <Reveal><FAQ /></Reveal>

      <FinalCTA />

      <MinimalFooter />

      <StickyMobileCTA />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Minimal header — one hairline rule, no floating card. Paid traffic gets the
// primary action, the date, and four escape hatches.
// ──────────────────────────────────────────────────────────────────────────
const MENU_LINKS = [
  { href: "/", label: "Acasă" },
  { href: "/relatii-si-cuplu", label: "Relații și cuplu" },
  { href: "/terapeuti", label: "Toți terapeuții" },
  { href: "/blog", label: "Blog" },
  { href: "/despre-noi", label: "Despre noi" },
];

function MinimalHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#264653]/10 bg-[#FFF9F5]/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1240px] items-center justify-between px-5 sm:h-[4.5rem] sm:px-8">
        <Link href="/" aria-label="Mindtale" className="flex items-center">
          <Image src="/logo.svg" alt="Mindtale" width={108} height={32} className="h-7 w-auto sm:h-8" priority />
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {MENU_LINKS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() =>
                trackEvent("webinar_lp_menu_click", {
                  webinar: "limite",
                  destination: item.href,
                  location: "desktop_inline",
                })
              }
              className="text-[14px] font-medium text-[#264653]/70 transition-colors hover:text-[#264653]"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden text-[13px] tabular-nums text-[#264653]/55 lg:inline">
            {WEBINAR.dateShort} · {WEBINAR.timeLabel}
          </span>
          <button
            type="button"
            onClick={() => scrollToForm("header")}
            className="hidden h-10 items-center gap-2 rounded-lg bg-[#DC7050] px-5 text-[14px] font-semibold text-white transition-colors hover:bg-[#c25c3e] sm:inline-flex"
          >
            Rezervă locul
          </button>
          <button
            type="button"
            aria-label={open ? "Închide meniul" : "Deschide meniul"}
            aria-expanded={open}
            onClick={() => {
              if (!open)
                trackEvent("webinar_lp_menu_open", { webinar: "limite", location: "header" });
              setOpen((p) => !p);
            }}
            className="inline-flex size-9 items-center justify-center text-[#264653]/70 transition-colors hover:text-[#264653] md:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#264653]/10 bg-[#FFF9F5] md:hidden">
          <nav className="mx-auto flex max-w-[1240px] flex-col px-5 py-2 sm:px-8">
            {MENU_LINKS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => {
                  trackEvent("webinar_lp_menu_click", {
                    webinar: "limite",
                    destination: item.href,
                  });
                  setOpen(false);
                }}
                className="flex items-center justify-between border-b border-[#264653]/8 py-3.5 text-[15px] font-medium text-[#264653] last:border-b-0"
              >
                {item.label}
                <ArrowRight className="size-4 text-[#264653]/35" />
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Hero — the question the webinar opens with, as the headline. The reel sits
// below the fold-line like the founder video on /sesiune-cunoastere, so the
// promise is read before the face.
// ──────────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="px-5 sm:px-8">
      <div className="mx-auto max-w-[900px] pb-14 pt-16 text-center sm:pb-20 sm:pt-24">
        <p className={eyebrowClass}>Webinar gratuit · {WEBINAR.dateLabel}</p>

        <h1 className="mx-auto mt-6 max-w-[18ch] font-serif text-[clamp(2.1rem,5vw,4rem)] font-normal leading-[1.08] tracking-[-0.015em] text-[#264653]">
          Ți-e greu să spui „nu”?
          <br className="hidden sm:block" /> Nu ține de caracter.
          <br className="hidden sm:block" /> Ține de un{" "}
          <span className="whitespace-nowrap">
            <span className="border-b-[0.06em] border-[#DC7050] pb-[0.04em]">
              tipar
            </span>
            .
          </span>
        </h1>

        <p className="mx-auto mt-7 max-w-[38rem] text-pretty text-[17px] leading-relaxed text-[#264653]/70 sm:mt-8 sm:text-[19px]">
          Limitele se învață în relații. Dacă nimeni nu ți-a arătat cum, e
          firesc să nu știi. Nouăzeci de minute în care înveți.
        </p>

        {/* When / where / how long, as a hairline strip: the three things a
            registrant checks before anything else. */}
        <dl className="mx-auto mt-10 grid max-w-[620px] grid-cols-1 gap-px border-y border-[#264653]/12 text-left sm:grid-cols-3">
          {[
            { k: "Când", v: `${WEBINAR.dateLabel}, ${WEBINAR.timeLabel}` },
            { k: "Cât", v: `${WEBINAR.durationLabel}, cu Q&A` },
            { k: "Unde", v: WEBINAR.format },
          ].map((row) => (
            <div
              key={row.k}
              className="border-b border-[#264653]/12 py-4 sm:border-b-0 sm:border-r sm:px-5 sm:first:pl-0 sm:last:border-r-0 sm:last:pr-0"
            >
              <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B]">
                {row.k}
              </dt>
              <dd className="mt-1.5 text-[16px] leading-snug text-[#264653]">
                {row.v}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-9 flex flex-col items-center">
          <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
            <button type="button" onClick={() => scrollToForm("hero")} className={btnPrimary}>
              Rezervă-ți locul
              <ArrowRight className="size-4" />
            </button>
            <a
              href="#program"
              onClick={() =>
                trackEvent("webinar_lp_agenda_click", { webinar: "limite", location: "hero" })
              }
              className={btnGhost}
            >
              Vezi programul
              <ChevronDown className="size-4" />
            </a>
          </div>
          <p className="mt-3.5 text-[13px] text-[#264653]/55">
            Gratuit · fără card · fără cont
          </p>
        </div>
      </div>

      {/* Speaker reel — captions are burned in, so muted autoplay carries the
          message; tapping unmutes, same as the founder video elsewhere. */}
      <SpeakerReel />
    </section>
  );
}

function SpeakerReel() {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  function toggleSound() {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) void v.play();
    trackEvent("webinar_reel_toggle_sound", { webinar: "limite", muted: v.muted });
  }

  return (
    <div className="mx-auto max-w-[1240px] border-t border-[#264653]/10 pb-16 pt-12 sm:pb-24 sm:pt-14">
      <figure className="grid items-center gap-8 sm:grid-cols-[268px_1fr] sm:gap-14">
        <div className="relative mx-auto aspect-[9/16] w-full max-w-[240px] overflow-hidden rounded-xl bg-[#264653]/8 ring-1 ring-[#264653]/10 sm:mx-0 sm:max-w-none">
          <video
            ref={ref}
            poster={`${WEBINAR.mediaBase}/ioana-limite-poster.jpg`}
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
            onClick={toggleSound}
            className="absolute inset-0 size-full cursor-pointer object-cover"
          >
            <source src='https://media.mindtale.ro/webinar-limite/ioana-limite.webm' type="video/webm" />
            <source src='https://media.mindtale.ro/webinar-limite/ioana-limite.mp4' type="video/mp4" />
          </video>
          <span className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-black/55 px-2 py-1 text-[10px] font-medium text-white backdrop-blur">
            {muted ? "Atinge pentru sunet" : "Sunet pornit"}
          </span>
        </div>

        <figcaption className="text-center sm:text-left">
          {/* Her own opening line, so the page says the same thing the ad said. */}
          <blockquote className="font-serif text-[clamp(1.25rem,2.6vw,1.9rem)] font-normal leading-[1.3] tracking-[-0.01em] text-[#264653]">
            „De câte ori ai acceptat ceva ce nu-ți doreai, doar ca să nu superi
            sau să nu dezamăgești pe cineva?”
          </blockquote>
          <p className="mt-6 text-[15px] text-[#264653]/70">
            <span className="font-semibold text-[#264653]">
              {WEBINAR.speakerFullName}
            </span>{" "}
            · {WEBINAR.speakerRole.toLowerCase()}
          </p>
          <p className="mt-1 text-[13px] text-[#264653]/50">
            40 de secunde · atinge videoul pentru sunet
          </p>
        </figcaption>
      </figure>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function TrustBand() {
  const items = [
    "Psiholog clinician acreditat COPSI",
    "Live, fără înregistrare publică",
    "Camera și microfonul rămân închise",
    "100% în română",
  ];
  return (
    <section className="border-y border-[#264653]/10">
      <ul className="mx-auto flex max-w-[1240px] flex-wrap items-center justify-center gap-x-8 gap-y-2 px-5 py-5 text-center text-[12px] text-[#264653]/55 sm:px-8">
        {items.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Self-identification — the statements participants actually pick in the
// opening poll. Nothing converts like reading your own sentence.
// ──────────────────────────────────────────────────────────────────────────
function Recognition() {
  const items = [
    "Îmi este greu să refuz pe cineva.",
    "Refuz, dar apoi mă simt vinovat.",
    "Pun limite abia când nu mai pot și izbucnesc.",
    "Mă tem că limita îl va supăra sau îndepărta pe celălalt.",
    "Uneori nici nu știu ce limită am.",
    "Spun „da” doar ca să nu dezamăgesc pe cineva.",
  ];

  return (
    <section className="px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-[1240px] gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className={eyebrowClass}>Prima întrebare a serii</p>
          <h2 className={`mt-5 ${h2Class}`}>
            Ți s-a întâmplat să spui „da” și, aproape în aceeași clipă, să simți
            în tine un „nu” foarte clar?
          </h2>
          <p className={`mt-5 max-w-md ${leadClass}`}>
            Așa începe webinarul. Alegi din lista de alături, anonim. Nu ca să te
            încadreze undeva, ci ca să vezi negru pe alb că nu ești singurul care
            bifează.
          </p>
        </div>

        <ol className="border-t border-[#264653]/10">
          {items.map((item, i) => (
            <li
              key={item}
              className="flex items-baseline gap-5 border-b border-[#264653]/10 py-5"
            >
              <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[16px] leading-relaxed text-[#264653]/85 sm:text-[17px]">
                {item}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function PullQuote() {
  return (
    <section className="bg-[#DC7050] px-5 py-20 text-white sm:px-8 sm:py-28">
      <figure className="mx-auto flex max-w-[820px] flex-col items-center text-center">
        {/* Three lines of display serif, so the measure is capped: at the top
            of this clamp the line would otherwise run past a comfortable
            reading width on a wide screen. */}
        <blockquote className="mx-auto max-w-[16ch] font-serif text-[clamp(1.6rem,3.4vw,2.6rem)] font-normal leading-[1.2] tracking-[-0.015em] sm:max-w-[22ch]">
          O limită spune ce îmi aparține, ce pot oferi și unde e nevoie să mă
          opresc.
        </blockquote>
        {/* First appearance of the figure the rest of the page reuses: two
            people and the edge between them, before anything happens to it. */}
        <BoundaryDiagram
          variant="neutru"
          accent="#FFFFFF"
          className="mt-12 text-white"
        />
      </figure>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// The one distinction that does most of the work in the webinar. Giving it
// away here is the point: it proves 90 minutes are worth it.
// ──────────────────────────────────────────────────────────────────────────
function TheDistinction() {
  return (
    <section className="px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-[1240px]">
        <div className="max-w-[52ch]">
          <p className={eyebrowClass}>Ideea din care pornim</p>
          <h2 className={`mt-5 ${h2Class}`}>
            Limita nu stabilește ce trebuie să facă celălalt. Stabilește ce voi
            face eu.
          </h2>
          <p className={`mt-5 max-w-xl ${leadClass}`}>
            Diferența pare de nuanță. E motivul pentru care unele limite se simt
            ca un conflict, iar altele pur și simplu țin.
          </p>
        </div>

        <div className="mt-14 grid gap-px border-t border-[#264653]/12 md:grid-cols-2">
          <div className="border-b border-[#264653]/12 py-8 md:border-b-0 md:border-r md:pr-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#264653]/45">
              Nu e limită
            </p>
            <BoundaryDiagram
              variant="control"
              accent="#264653"
              className="mt-6 opacity-45"
            />
            <p className="mt-7 font-serif text-[clamp(1.15rem,2.2vw,1.5rem)] font-normal leading-snug text-[#264653]/55">
              „Tu nu ai voie să-mi mai scrii după ora 20.”
            </p>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-[#264653]/70">
              Încearcă să controleze comportamentul celuilalt. De aceea se simte
              ca o interdicție, și de aceea rareori ține.
            </p>
          </div>

          <div className="py-8 md:pl-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B]">
              E limită
            </p>
            <BoundaryDiagram variant="limita" accent="#DC7050" className="mt-6" />
            <p className="mt-7 font-serif text-[clamp(1.15rem,2.2vw,1.5rem)] font-normal leading-snug text-[#264653]">
              „După ora 20 nu mai răspund mesajelor de serviciu.”
            </p>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-[#264653]/70">
              Exprimă ce vei face tu. Nu ai nevoie de acordul nimănui ca să o
              susții, și nu e nici pedeapsă, nici respingere.
            </p>
          </div>
        </div>

        <p className="mt-14 text-[15px] leading-relaxed text-[#264653]/70">
          În webinar mergem mai departe și separăm trei lucruri pe care le
          confundăm constant:
        </p>

        <dl className="mt-6 grid gap-px border-t border-[#264653]/12 md:grid-cols-3">
          {[
            { k: "Cererea", v: "„Te rog să nu ridici vocea.”" },
            { k: "Limita", v: "„Nu voi continua conversația dacă țipi.”" },
            {
              k: "Acțiunea de protecție",
              v: "„Dacă țipi, mă retrag și revin când putem vorbi calm.”",
            },
          ].map((t) => (
            <div
              key={t.k}
              className="border-b border-[#264653]/12 py-6 md:border-b-0 md:border-r md:px-7 md:first:pl-0 md:last:border-r-0 md:last:pr-0"
            >
              <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B]">
                {t.k}
              </dt>
              <dd className="mt-3 text-[16px] leading-relaxed text-[#264653]/85 sm:text-[17px]">
                {t.v}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// The one dark stretch of the page. Three patterns, three columns: the most
// "textbook" content on the page, so it gets the strongest visual break.
// ──────────────────────────────────────────────────────────────────────────
function ThreePatterns() {
  const patterns = [
    {
      n: "01",
      variant: "permeabile" as const,
      title: "Prea permeabile",
      lines: [
        "spunem „da” automat",
        "preluăm responsabilitățile altora",
        "tolerăm intruziuni",
        "ne adaptăm până ne pierdem preferințele",
      ],
    },
    {
      n: "02",
      variant: "rigide" as const,
      title: "Foarte rigide",
      lines: [
        "evităm vulnerabilitatea",
        "nu cerem și nu acceptăm ajutor",
        "păstrăm distanța ca să nu fim răniți",
        "confundăm independența cu izolarea",
      ],
    },
    {
      n: "03",
      variant: "flexibile" as const,
      title: "Flexibile și sănătoase",
      lines: [
        "putem spune atât „da”, cât și „nu”",
        "ținem cont de context fără să ne anulăm nevoile",
        "tolerăm că celălalt poate fi dezamăgit",
        "negociem fără să renunțăm la esențial",
      ],
    },
  ];

  return (
    <section className="bg-[#264653] px-5 py-20 text-white sm:px-8 sm:py-28">
      <div className="mx-auto max-w-[1240px]">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#6FBFC3]">
            Trei tipare
          </p>
          <h2 className="mx-auto mt-5 max-w-[24ch] font-serif text-[clamp(1.6rem,3.4vw,2.6rem)] font-medium leading-[1.12] tracking-[-0.015em]">
            Sunt trei. Îl recunoști pe al tău din prima coloană sau din a doua.
          </h2>
        </div>

        <ol className="mt-14 grid gap-px border-t border-white/18 sm:grid-cols-3">
          {patterns.map((p) => (
            <li
              key={p.n}
              className="border-b border-white/18 py-8 sm:border-b-0 sm:border-r sm:px-7 sm:first:pl-0 sm:last:border-r-0 sm:last:pr-0"
            >
              {/* Same figure as "Ideea din care pornim", in a third state, so
                  the pattern is read rather than only described. */}
              <BoundaryDiagram variant={p.variant} accent="#6FBFC3" className="mb-7" />
              <span className="font-serif text-[15px] tabular-nums text-[#6FBFC3]">
                {p.n}
              </span>
              <h3 className="mt-3 font-serif text-[19px] font-medium leading-snug sm:text-[21px]">
                {p.title}
              </h3>
              <ul className="mt-4 space-y-2">
                {p.lines.map((l) => (
                  <li key={l} className="text-[15px] leading-relaxed text-white/72">
                    {l}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <p className="mx-auto mt-12 max-w-[52ch] text-center text-[15px] leading-relaxed text-white/65">
          Limitele se formează în copilărie, se testează în adolescență și se
          joacă mai departe în viața adultă. Se pot reînvăța la orice vârstă.
        </p>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function WhatYouLeaveWith() {
  const items = [
    {
      title: "Știi ce e o limită și ce nu e",
      body: "Nu e pedeapsă, nu e răzbunare, nu e egoism. Și vezi în care dintre cele trei tipare te așezi tu.",
    },
    {
      title: "Îți recunoști „galbenul” din timp",
      body: "Un semafor pentru corp, ca să nu mai pui limita abia când ai ajuns la capăt.",
    },
    {
      title: "Ai o structură în trei pași",
      body: "„Când se întâmplă..., am nevoie de... De aceea, voi / nu voi... Dacă situația continuă, voi...”",
    },
    {
      title: "Pleci cu o limită scrisă de tine",
      body: "Alegi o arie din viața ta și formulezi limita live, clar, fără agresivitate și fără explicații excesive.",
    },
  ];

  return (
    <section className="px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-[1240px] gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className={eyebrowClass}>Ce iei cu tine</p>
          <h2 className={`mt-5 ${h2Class}`}>
            Pleci cu o frază pe care o poți spune, nu cu un concept.
          </h2>
          <p className={`mt-5 max-w-md ${leadClass}`}>
            Obiectivul serii e concret: la final identifici o arie a vieții tale
            în care ai nevoie de o limită și o formulezi clar.
          </p>
        </div>

        <ol className="border-t border-[#264653]/10">
          {items.map((item, i) => (
            <li
              key={item.title}
              className="flex items-baseline gap-5 border-b border-[#264653]/10 py-6"
            >
              <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-serif text-[19px] font-medium leading-snug text-[#264653] sm:text-[21px]">
                  {item.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#264653]/70 sm:text-[16px]">
                  {item.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function Semafor() {
  const lights = [
    { color: "#2E9E58", label: "Verde", body: "Mă simt confortabil și aleg liber." },
    { color: "#E9C46A", label: "Galben", body: "Apare tensiune, oboseală, iritare sau ezitare." },
    { color: "#DC7050", label: "Roșu", body: "Simt că sunt forțat, invadat, epuizat sau că mă trădez." },
  ];
  return (
    <section className="border-y border-[#264653]/10 px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-[820px] text-center">
        <p className={eyebrowClass}>Un instrument din webinar</p>
        <h2 className={`mx-auto mt-5 max-w-[20ch] ${h2Class}`}>
          Corpul știe înaintea ta.
        </h2>

        <ul className="mt-12 border-t border-[#264653]/12 text-left">
          {lights.map((l) => (
            <li
              key={l.label}
              className="flex items-baseline gap-4 border-b border-[#264653]/12 py-5"
            >
              <span
                className="mt-[6px] size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: l.color }}
                aria-hidden
              />
              <span className="w-[4.5rem] shrink-0 text-[15px] font-semibold text-[#264653]">
                {l.label}
              </span>
              <span className="text-[16px] leading-relaxed text-[#264653]/80 sm:text-[17px]">
                {l.body}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-10 font-serif text-[clamp(1.15rem,2.4vw,1.6rem)] font-normal leading-snug text-[#DC7050]">
          În ce situație din viața ta corpul spune „galben”, dar tu continui să
          spui „da”?
        </p>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Speaker — the trust anchor. One real quote from her own material; no
// fabricated testimonials (COPSI).
// ──────────────────────────────────────────────────────────────────────────
function Speaker() {
  const credentials = [
    "Psiholog clinician, Universitatea Spiru Haret",
    "Psihoterapeut psihanalitic, INSIGHT",
    "Psiho-oncolog",
  ];
  return (
    <section className="px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-[1240px] items-start gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div>
          <div className="relative aspect-[3/4] w-full max-w-[320px] overflow-hidden rounded-xl bg-[#264653]/8 ring-1 ring-[#264653]/10">
            <Image
              src="/webinar-limite/ioana-ivan.jpg"
              alt={`${WEBINAR.speakerFullName}, ${WEBINAR.speakerRole}`}
              fill
              className="object-cover object-top"
              sizes="(max-width: 1024px) 320px, 380px"
            />
          </div>
        </div>

        <div>
          <p className={eyebrowClass}>Cine susține webinarul</p>
          <h2 className={`mt-5 ${h2Class}`}>{WEBINAR.speakerFullName}</h2>
          <p className="mt-3 text-[15px] font-medium text-[#2C969B]">
            {WEBINAR.speakerRole}
          </p>

          <p className={`mt-6 max-w-xl ${leadClass}`}>
            Lucrează cu adulți care se confruntă cu anxietate, stres, dificultăți
            relaționale sau profesionale, sprijinind procesul de autocunoaștere
            și dezvoltarea unor modalități mai sănătoase de a face față
            provocărilor vieții.
          </p>

          <blockquote className="mt-8 border-l-2 border-[#DC7050] pl-5 font-serif text-[clamp(1.15rem,2.2vw,1.5rem)] font-normal leading-snug text-[#264653]">
            „Limita nu este o respingere a celuilalt, ci o formă de claritate.”
          </blockquote>

          <ul className="mt-8 border-t border-[#264653]/10">
            {credentials.map((c) => (
              <li
                key={c}
                className="border-b border-[#264653]/10 py-3 text-[15px] text-[#264653]/75"
              >
                {c}
              </li>
            ))}
          </ul>

          <Link
            href={WEBINAR.speakerProfileUrl}
            onClick={() => trackEvent("webinar_lp_speaker_click", { webinar: "limite" })}
            className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-medium text-[#2C969B] underline decoration-[#2C969B]/30 underline-offset-4 transition-colors hover:decoration-[#2C969B]"
          >
            Vezi profilul complet al Ioanei
            <ArrowUpRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function Agenda() {
  const blocks = [
    { title: "Ce este o limită și ce nu este" },
    { title: "Trei tipare: permeabile, rigide, flexibile" },
    {
      title: "De ce spunem „da” când vrem „nu”",
      note: "Vinovăție, frica de respingere și nevoia de a fi „cel bun”.",
    },
    { title: "Limită, cerere sau acțiune de protecție" },
    {
      title: "Ariile vieții în care avem nevoie de limite",
      note: "Corp, emoții, timp, muncă, familie, digital și bani.",
    },
    { title: "Semaforul: verde, galben, roșu" },
    {
      title: "Exercițiu: formulează limita ta",
      note: "Două minute în care scrii o frază pe care o poți spune.",
    },
    {
      title: "Patru situații reale",
      note: "La serviciu, în familie, în cuplu, între prieteni.",
    },
    { title: "Întrebări și răspunsuri", note: "Întrebi în scris, anonim dacă preferi." },
  ];

  return (
    <section id="program" className="scroll-mt-24 border-t border-[#264653]/10 px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-[1240px] gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className={eyebrowClass}>Programul serii</p>
          <h2 className={`mt-5 ${h2Class}`}>Ce parcurgem, în {WEBINAR.durationLabel}.</h2>
          <p className={`mt-5 max-w-md ${leadClass}`}>
            {WEBINAR.dateLabel}, de la {WEBINAR.timeLabel}. {WEBINAR.format}.
            Vii cu o cafea, pleci cu o frază pe care o poți spune.
          </p>
          <button
            type="button"
            onClick={() => scrollToForm("agenda")}
            className={`mt-8 ${btnPrimary}`}
          >
            Rezervă-ți locul
            <ArrowRight className="size-4" />
          </button>
        </div>

        <ol className="border-t border-[#264653]/10">
          {blocks.map((b, i) => (
            <li
              key={b.title}
              className="flex items-baseline gap-5 border-b border-[#264653]/10 py-5"
            >
              <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="text-[16px] font-medium leading-snug text-[#264653] sm:text-[17px]">
                  {b.title}
                </h3>
                {b.note && (
                  <p className="mt-1.5 text-[14px] leading-relaxed text-[#264653]/60">
                    {b.note}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Registration
// ──────────────────────────────────────────────────────────────────────────
function useWebinarForm(location: string) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // <PhoneInput> stores E.164 already; isValidPhoneNumber checks it against the
  // real numbering plan of whichever country the user picked, not just RO.
  const phoneOk = Boolean(phone) && isValidPhoneNumber(phone);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (website) return; // bot caught by honeypot
    if (!firstName.trim() || !lastName.trim()) {
      setError("Completează numele și prenumele.");
      return;
    }
    if (!emailOk) {
      setError("Verifică adresa de email.");
      return;
    }
    if (!phoneOk) {
      setError("Verifică numărul de telefon, inclusiv prefixul de țară.");
      return;
    }
    if (!gdpr) {
      setError("Trebuie să accepți politica de confidențialitate.");
      return;
    }

    setStatus("loading");
    try {
      let token: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        token = (await recaptchaRef.current?.executeAsync()) ?? undefined;
        recaptchaRef.current?.reset();
        if (!token) {
          setError("Verificarea anti-spam a eșuat. Reîncearcă.");
          setStatus("idle");
          return;
        }
      }

      const res = await fetch("/api/webinar-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone,
          source: WEBINAR.source,
          token,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Ceva n-a mers. Mai încearcă o dată.");
        setStatus("idle");
        return;
      }

      // One Lead to GA4 + Meta + Google Ads, deduped by a shared event id.
      const leadId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      const em = email.trim();
      trackEvent("webinar_register_submit", { webinar: "limite", location });
      trackMetaStandardEvent("Lead", { content_name: WEBINAR.source }, leadId, {
        email: em,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone,
      });
      trackGoogleAdsLead("webinar", { userEmail: em, transactionId: leadId });

      setStatus("done");
    } catch {
      setError("Ceva n-a mers. Mai încearcă o dată în câteva secunde.");
      setStatus("idle");
    }
  }

  return {
    firstName, setFirstName, lastName, setLastName, email, setEmail,
    phone, setPhone, gdpr, setGdpr, website, setWebsite,
    status, error, submit, recaptchaRef,
  };
}

function Registration() {
  return (
    <section id="inscriere" className="scroll-mt-24 border-t border-[#264653]/10 px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-[1240px] gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div>
          <p className={eyebrowClass}>Înscriere</p>
          <h2 className={`mt-5 ${h2Class}`}>Rezervă-ți locul. E gratuit.</h2>
          <p className={`mt-5 max-w-md ${leadClass}`}>
            Lasă-ne emailul. Îți trimitem linkul de acces, invitația de calendar
            și un reminder scurt înainte să înceapă.
          </p>

          <dl className="mt-10 border-t border-[#264653]/12">
            {[
              { k: "Webinarul", v: WEBINAR.title },
              { k: "Data", v: `${WEBINAR.dateLabel}, ora ${WEBINAR.timeLabel} (ora României)` },
              { k: "Durata", v: `${WEBINAR.durationLabel}, cu întrebări la final` },
              { k: "Unde", v: `${WEBINAR.format}. Primești linkul pe email.` },
              { k: "Preț", v: "Gratuit. Fără card, fără cont." },
            ].map((row) => (
              <div
                key={row.k}
                className="flex flex-col gap-1 border-b border-[#264653]/12 py-4 sm:flex-row sm:gap-6"
              >
                <dt className="w-28 shrink-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B] sm:pt-1">
                  {row.k}
                </dt>
                <dd className="text-[15px] leading-relaxed text-[#264653]/85 sm:text-[16px]">
                  {row.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <WebinarForm location="section" />
      </div>
    </section>
  );
}

function WebinarForm({ location }: { location: string }) {
  const f = useWebinarForm(location);

  if (f.status === "done") return <SuccessCard email={f.email} />;

  const inputCls =
    "mt-2 h-12 w-full rounded-lg border border-[#264653]/20 bg-white px-4 text-[15px] text-[#264653] placeholder:text-[#264653]/35 outline-none transition-colors focus:border-[#2C969B]";

  return (
    <form onSubmit={f.submit} className="lg:pt-[3.25rem]">
      {/* Honeypot — hidden from humans */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={f.website}
        onChange={(e) => f.setWebsite(e.target.value)}
        className="absolute left-[-9999px] size-0"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block text-[13px] font-medium text-[#264653]/75">
          Prenume
          <input
            type="text"
            value={f.firstName}
            onChange={(e) => f.setFirstName(e.target.value)}
            placeholder="Ioana"
            autoComplete="given-name"
            className={inputCls}
          />
        </label>
        <label className="block text-[13px] font-medium text-[#264653]/75">
          Nume
          <input
            type="text"
            value={f.lastName}
            onChange={(e) => f.setLastName(e.target.value)}
            placeholder="Popescu"
            autoComplete="family-name"
            className={inputCls}
          />
        </label>
      </div>

      <label className="mt-5 block text-[13px] font-medium text-[#264653]/75">
        Email
        <input
          type="email"
          value={f.email}
          onChange={(e) => f.setEmail(e.target.value)}
          placeholder="nume@email.com"
          autoComplete="email"
          className={inputCls}
        />
      </label>

      <div className="mt-5 block text-[13px] font-medium text-[#264653]/75">
        <label htmlFor="webinar-phone">Telefon</label>
        {/* Country picker + per-country validation: a good part of the audience
            is diaspora, so a Romanian-only field would reject real numbers. */}
        <PhoneInput
          id="webinar-phone"
          international
          defaultCountry="RO"
          labels={PHONE_LABELS}
          value={f.phone || undefined}
          onChange={(v) => f.setPhone(v ?? "")}
          countrySelectComponent={CountrySelectWithSearch}
          countrySelectProps={{
            triggerClassName:
              "flex h-12 items-center gap-1.5 rounded-lg border border-[#264653]/20 bg-white px-3 text-[15px] text-[#264653] transition-colors hover:bg-white focus:border-[#2C969B] focus:outline-none",
          }}
          className="mt-2 flex gap-2"
          numberInputProps={{
            placeholder: "+40 7xx xxx xxx",
            autoComplete: "tel",
            // 16px stops iOS Safari from auto-zooming the page on focus.
            className:
              "h-12 w-full min-w-0 rounded-lg border border-[#264653]/20 bg-white px-4 text-base text-[#264653] placeholder:text-[#264653]/35 outline-none transition-colors focus:border-[#2C969B]",
          }}
        />
        <span className="mt-2 block text-[12.5px] font-normal text-[#264653]/55">
          Doar pentru reminderul de dinaintea webinarului.
        </span>
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-2.5 text-[12.5px] leading-relaxed text-[#264653]/70">
        <input
          type="checkbox"
          checked={f.gdpr}
          onChange={(e) => f.setGdpr(e.target.checked)}
          className="mt-0.5 size-4 shrink-0 rounded border-[#264653]/30 text-[#2C969B] focus:ring-[#2C969B]/40"
        />
        <span>
          Sunt de acord cu prelucrarea datelor conform{" "}
          <a
            href={GDPR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#2C969B] underline underline-offset-2"
          >
            politicii de confidențialitate
          </a>
          .
        </span>
      </label>

      {f.error && (
        <p className="mt-5 border-l-2 border-[#DC7050] pl-4 text-[13.5px] font-medium text-[#c25c3e]">
          {f.error}
        </p>
      )}

      <button
        type="submit"
        disabled={f.status === "loading"}
        className={`mt-6 w-full ${btnPrimary} disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {f.status === "loading" ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Se trimite...
          </>
        ) : (
          <>
            Rezervă-mi locul gratuit
            <ArrowRight className="size-4" />
          </>
        )}
      </button>

      <p className="mt-3.5 text-center text-[13px] text-[#264653]/55">
        Fără card · linkul de acces ajunge pe email
      </p>

      {RECAPTCHA_SITE_KEY && (
        <ReCAPTCHA
          ref={f.recaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          size="invisible"
          badge="bottomleft"
        />
      )}
    </form>
  );
}

function SuccessCard({ email }: { email: string }) {
  return (
    <div className="border-t border-[#264653]/12 pt-8 lg:pt-[4rem]">
      <span className="flex size-10 items-center justify-center rounded-full bg-[#2C969B]/12">
        <Check className="size-5 text-[#2C969B]" />
      </span>
      <h3 className={`mt-5 ${h2Class}`}>Gata, ești înscris.</h3>
      <p className={`mt-4 max-w-md ${leadClass}`}>
        Ți-am trimis confirmarea pe {email}, cu invitația de calendar. Ne vedem{" "}
        {WEBINAR.dateLabel.toLowerCase()}, la {WEBINAR.timeLabel}.
      </p>
      <a
        href={googleCalUrl()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("webinar_add_to_calendar", { webinar: "limite" })}
        className={`mt-7 ${btnGhost}`}
      >
        Adaugă în calendar
        <ArrowUpRight className="size-4" />
      </a>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: "E gratuit cu adevărat?",
    a: "Da, de la cap la coadă. Nu cerem card și nu apare nicio plată mai târziu. Te înscrii, primești linkul pe email, participi.",
  },
  {
    q: "Pentru cine e webinarul?",
    a: "Pentru tine, dacă îți e greu să refuzi, dacă te simți vinovat după ce pui o limită sau dacă ajungi să spui „nu” abia când ai explodat. Nu are legătură doar cu relația de cuplu: vorbim și despre muncă, familie, prieteni și spațiul digital.",
  },
  {
    q: "Trebuie să apar pe cameră sau să vorbesc?",
    a: "Nu. Stai cu camera și microfonul închise cât vrei. Exercițiile le faci pentru tine, iar întrebările le poți scrie, anonim dacă preferi. Nimeni nu te pune pe loc.",
  },
  {
    q: "Se înregistrează webinarul?",
    a: "Nu, e doar live. Așa discuțiile și întrebările rămân între participanți. Blochează-ți ora în calendar ca să nu o ratezi.",
  },
  {
    q: "Am nevoie de pregătire înainte?",
    a: "Nu. E util să vii cu o situație concretă în minte, una în care simți că ai nevoie de o limită, dar o poți alege și în timpul webinarului.",
  },
  {
    q: "De ce îmi cereți numărul de telefon?",
    a: "Ca să-ți trimitem un reminder scurt înainte de webinar, fiindcă multe linkuri se pierd în inbox. Datele tale sunt protejate conform GDPR și nu le dăm nimănui.",
  },
  {
    q: "E o ședință de terapie?",
    a: "Nu, e un webinar educativ, de grup. Nu înlocuiește terapia, dar te poate ajuta să-ți dai seama dacă și când ți-ar prinde bine un psihoterapeut.",
  },
];

function FAQ() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <section className="border-t border-[#264653]/10 px-5 py-20 sm:px-8 sm:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="mx-auto max-w-[760px]">
        <div className="text-center">
          <p className={eyebrowClass}>Întrebări</p>
          <h2 className={`mt-5 ${h2Class}`}>Ce te-ai putea întreba.</h2>
        </div>

        <div className="mt-12 border-t border-[#264653]/12">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem key={item.q} q={item.q} a={item.a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="group border-b border-[#264653]/12"
      onToggle={(e) => {
        const isOpen = (e.target as HTMLDetailsElement).open;
        setOpen(isOpen);
        if (isOpen)
          trackEvent("webinar_lp_faq_open", { webinar: "limite", index, question: q });
      }}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[16px] font-medium text-[#264653] [&::-webkit-details-marker]:hidden sm:text-[17px]">
        {q}
        <ChevronDown
          className={`size-4 shrink-0 text-[#264653]/40 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </summary>
      <p className="pb-5 pr-10 text-[15px] leading-relaxed text-[#264653]/70 sm:text-[16px]">
        {a}
      </p>
    </details>
  );
}

// ──────────────────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="bg-[#264653] px-5 py-20 text-white sm:px-8 sm:py-28">
      <div className="mx-auto max-w-[820px] text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#6FBFC3]">
          {WEBINAR.dateLabel} · {WEBINAR.timeLabel}
        </p>
        <h2 className="mx-auto mt-5 max-w-[22ch] font-serif text-[clamp(1.7rem,3.6vw,2.8rem)] font-medium leading-[1.12] tracking-[-0.015em]">
          O seară acum, ca să nu mai spui „da” cu resentiment luni întregi.
        </h2>
        <p className="mx-auto mt-6 max-w-[46rem] text-[16px] leading-relaxed text-white/70 sm:text-[17px]">
          {WEBINAR.durationLabel}, {WEBINAR.format.toLowerCase()}. Gratuit, fără
          card, cu întrebări la final.
        </p>
        <div className="mt-9 flex justify-center">
          <button
            type="button"
            onClick={() => scrollToForm("final")}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#DC7050] px-7 text-[15px] font-semibold text-white transition-colors hover:bg-[#c25c3e]"
          >
            Rezervă-ți locul gratuit
            <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function MinimalFooter() {
  return (
    <footer className="border-t border-[#264653]/10 px-5 py-10 sm:px-8">
      <div className="mx-auto flex max-w-[1240px] flex-col items-center gap-3 text-center text-[12px] text-[#264653]/55 sm:flex-row sm:justify-between sm:gap-0 sm:text-left">
        <p>
          © {new Date().getFullYear()} Mindtale, marcă a Zenyra Therapy SRL.
          Terapeuții sunt acreditați COPSI.
        </p>
        <div className="flex items-center gap-5">
          <Link href="/termeni-si-conditii" className="transition-colors hover:text-[#264653]">
            Termeni
          </Link>
          <Link href="/politica-gdpr" className="transition-colors hover:text-[#264653]">
            Confidențialitate
          </Link>
          <Link href="/contact" className="transition-colors hover:text-[#264653]">
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#264653]/10 bg-[#FFF9F5]/95 px-4 py-3 backdrop-blur sm:hidden">
      <button
        type="button"
        onClick={() => scrollToForm("sticky_mobile")}
        className={`w-full ${btnPrimary}`}
      >
        Rezervă-ți locul · {WEBINAR.dateShort}
        <ArrowRight className="size-4" />
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Closed state — shown once the event has started. Registrations are over, so
// the page sends visitors to Ioana, or to a free intro call with anyone.
// ──────────────────────────────────────────────────────────────────────────
function ClosedPage() {
  const next = [
    {
      href: WEBINAR.speakerProfileUrl,
      eyebrow: "Cu Ioana Ivan",
      title: "Vorbește cu Ioana",
      body: "Vezi profilul ei și programează o discuție gratuită de 20 de minute, ca să vezi dacă vă potriviți.",
      event: "webinar_closed_speaker",
    },
    {
      href: "/sesiune-cunoastere",
      eyebrow: "Gratuit · 20 de minute",
      title: "O discuție de cunoaștere cu un terapeut",
      body: "Alegi un psihoterapeut și vorbiți 20 de minute, gratuit.",
      event: "webinar_closed_discovery",
    },
    {
      href: "/terapeuti",
      eyebrow: "Psihoterapeuți acreditați COPSI",
      title: "Toți terapeuții Mindtale",
      body: "Caută după specializare, preț și disponibilitate.",
      event: "webinar_closed_therapists",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <header className="border-b border-[#264653]/10">
        <div className="mx-auto flex h-16 max-w-[1240px] items-center justify-between px-5 sm:h-[4.5rem] sm:px-8">
          <Link href="/" aria-label="Mindtale" className="flex items-center">
            <Image src="/logo.svg" alt="Mindtale" width={108} height={32} className="h-7 w-auto sm:h-8" priority />
          </Link>
          <Link
            href="/terapeuti"
            className="text-[14px] font-medium text-[#264653]/70 transition-colors hover:text-[#264653]"
          >
            Vezi terapeuții
          </Link>
        </div>
      </header>

      <section className="px-5 sm:px-8">
        <div className="mx-auto max-w-[900px] pb-16 pt-20 text-center sm:pb-20 sm:pt-28">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#264653]/45">
            Înscrieri închise
          </p>
          <h1 className="mx-auto mt-6 max-w-[22ch] font-serif text-[clamp(2rem,4.6vw,3.5rem)] font-normal leading-[1.08] tracking-[-0.015em] text-[#264653]">
            Înscrierile la webinarul despre limite s-au închis.
          </h1>
          <p className="mx-auto mt-7 max-w-[46rem] text-pretty text-[16px] leading-relaxed text-[#264653]/70 sm:text-[17px]">
            Webinarul a avut loc pe 30 septembrie, cu Ioana Ivan. Dacă vrei să
            lucrezi pe limitele tale cu un terapeut, poți începe cu o discuție
            gratuită de 20 de minute.
          </p>
        </div>
      </section>

      <section className="border-t border-[#264653]/10 px-5 pb-20 sm:px-8 sm:pb-28">
        <ol className="mx-auto max-w-[820px]">
          {next.map((item, i) => (
            <li key={item.href} className="border-b border-[#264653]/10">
              <Link
                href={item.href}
                onClick={() => trackEvent(item.event, { from: "webinar_limite_closed" })}
                className="group flex items-baseline gap-5 py-7"
              >
                <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B]">
                    {item.eyebrow}
                  </span>
                  <span className="mt-2 block font-serif text-[19px] font-medium leading-snug text-[#264653] sm:text-[22px]">
                    {item.title}
                  </span>
                  <span className="mt-2 block text-[15px] leading-relaxed text-[#264653]/70 sm:text-[16px]">
                    {item.body}
                  </span>
                </span>
                <ArrowRight className="mt-1 size-4 shrink-0 text-[#264653]/35 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <footer className="border-t border-[#264653]/10 px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center gap-3 text-center text-[12px] text-[#264653]/55 sm:flex-row sm:justify-between sm:gap-0 sm:text-left">
          <p>
            © {new Date().getFullYear()} Mindtale, marcă a Zenyra Therapy SRL.
            Terapeuții sunt acreditați COPSI.
          </p>
          <div className="flex items-center gap-5">
            <Link href="/termeni-si-conditii" className="transition-colors hover:text-[#264653]">
              Termeni
            </Link>
            <Link href="/politica-gdpr" className="transition-colors hover:text-[#264653]">
              Confidențialitate
            </Link>
            <Link href="/contact" className="transition-colors hover:text-[#264653]">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
